import { WebEngageCommonEventData } from '@/types/wegengage';
import { createContext } from './createContext';
import { commonAction } from '@/types/commonActions';
import { Dispatch, useContext } from 'react';

type DisptachAction<T> = (d: Dispatch<commonAction>) => T;

export type WebengageContextState = {
  webengageDefault: Pick<
    WebEngageCommonEventData,
    'loggedin' | 'existing_user' | 'dashboard_screen_view' | 'amc_multi_tier_flow'
  >;
  scriptLoaded: boolean;
};

export type WebEngageDispatchType = {
  updateDashboardScreenView: DisptachAction<(view: 'mcc' | 'scc') => void>;
  webengageScriptInitialized: any;
};

enum WebEngageActions {
  UPDATE_DASHBOARD_SCREEN_VIEW = 'UPDATE_DASHBOARD_SCREEN_VIEW',
  WEBENGAGE_SCRIPT_INTIALIZED = 'WEBENGAGE_SCRIPT_INTIALIZED',
}

const actionNames: Record<keyof WebEngageDispatchType, WebEngageActions> = {
  updateDashboardScreenView: WebEngageActions.UPDATE_DASHBOARD_SCREEN_VIEW,
  webengageScriptInitialized: WebEngageActions.WEBENGAGE_SCRIPT_INTIALIZED,
};

const initialState: WebengageContextState = {
  webengageDefault: {
    loggedin: true,
    existing_user: true,
    dashboard_screen_view: 'mcc',
    amc_multi_tier_flow: 'Yes',
  },
  scriptLoaded: false,
};

export const webengageReducer = (
  state: WebengageContextState,
  action: commonAction,
): WebengageContextState => {
  switch (action.type as unknown as WebEngageActions) {
    case actionNames.updateDashboardScreenView:
      return {
        ...state,
        webengageDefault: {
          ...state.webengageDefault,
          dashboard_screen_view: action.payload.dashboard_screen_view,
        },
      };
    case actionNames.webengageScriptInitialized:
      return {
        ...state,
        scriptLoaded: action.payload.scriptLoaded,
      };
    default:
      throw new Error(`Unsupported type: ${action.type}`);
  }
};

const updateDashboardScreenView =
  (dispatch: Dispatch<commonAction>) => (dashboard_screen_view: 'mcc' | 'scc') => {
    dispatch({
      type: WebEngageActions.UPDATE_DASHBOARD_SCREEN_VIEW,
      payload: { dashboard_screen_view },
    });
  };

const webengageScriptInitialized =
  (dispatch: Dispatch<commonAction>) => (scriptLoaded: boolean) => {
    dispatch({
      type: WebEngageActions.WEBENGAGE_SCRIPT_INTIALIZED,
      payload: { scriptLoaded },
    });
  };

const { Context: WebEngageContext, Provider: WebEngageProvider } = createContext<
  WebengageContextState,
  WebEngageDispatchType
>(
  webengageReducer,
  {
    updateDashboardScreenView,
    webengageScriptInitialized,
  },
  initialState,
);

WebEngageContext.displayName = 'WebEngage';

export const useWebengageContext = () => useContext(WebEngageContext);

export { WebEngageContext, WebEngageProvider };
